/**
 * Unified blockchain chain configuration.
 * Single source of truth for all chain-related mappings across the codebase.
 *
 * Used by:
 * - API: tokens-for-swap validation, Mobula API calls, address normalization
 * - Frontend: chain logos (CDN), display names, chain selection UI
 *
 * To add a new chain:
 * 1. Add entry to CHAIN_CONFIG
 * 2. Add chain logo to CDN (cdn.cedelabs.io/network/{internalId}.png)
 *
 * All other mappings are auto-derived from CHAIN_CONFIG.
 */
import type { InternalChainId } from "@cede/types";

/**
 * Chain configuration entry
 */
export interface ChainConfigEntry {
  /** Chain ID as string - EVM chain ID or non-EVM identifier (e.g., "8453", "solana") */
  chainId: string;
  /** EVM chain ID as number (0 for non-EVM chains like Solana) */
  evmChainId: number;
  /** Internal chain ID for CDN logos (e.g., "base", "ethereum", "solana") */
  internalId: InternalChainId;
  /** Mobula API blockchain name (e.g., "base", "ethereum", "solana") */
  mobulaName: string;
  /** Display name for UI (e.g., "Base", "Ethereum", "Solana") */
  displayName: string;
  /** Whether this chain is EVM-compatible */
  isEvm: boolean;
  /** Optional block explorer URL */
  explorerUrl?: string;
}

export interface WrappedNativeTokenConfig {
  address: string;
  symbol: string;
  name: string;
}

/**
 * Central chain configuration - single source of truth
 * Ordered by priority (Solana first for DEX prominence, then alphabetically)
 */
export const CHAIN_CONFIG: ChainConfigEntry[] = [
  // Non-EVM chains (Solana first for DEX prominence)
  {
    chainId: "solana",
    evmChainId: 0,
    internalId: "solana",
    mobulaName: "solana",
    displayName: "Solana",
    isEvm: false,
    explorerUrl: "https://solscan.io",
  },
  // EVM chains (alphabetical by display name)
  {
    chainId: "42161",
    evmChainId: 42161,
    internalId: "arbitrum",
    mobulaName: "arbitrum",
    displayName: "Arbitrum",
    isEvm: true,
    explorerUrl: "https://arbiscan.io",
  },
  {
    chainId: "43114",
    evmChainId: 43114,
    internalId: "avalanche",
    mobulaName: "avalanche",
    displayName: "Avalanche",
    isEvm: true,
    explorerUrl: "https://snowtrace.io",
  },
  {
    chainId: "8453",
    evmChainId: 8453,
    internalId: "base",
    mobulaName: "base",
    displayName: "Base",
    isEvm: true,
    explorerUrl: "https://basescan.org",
  },
  {
    chainId: "56",
    evmChainId: 56,
    internalId: "bsc",
    mobulaName: "bsc",
    displayName: "BNB Chain",
    isEvm: true,
    explorerUrl: "https://bscscan.com",
  },
  {
    chainId: "1",
    evmChainId: 1,
    internalId: "ethereum",
    mobulaName: "ethereum",
    displayName: "Ethereum",
    isEvm: true,
    explorerUrl: "https://etherscan.io",
  },
  {
    chainId: "250",
    evmChainId: 250,
    internalId: "fantom",
    mobulaName: "fantom",
    displayName: "Fantom",
    isEvm: true,
    explorerUrl: "https://ftmscan.com",
  },
  {
    chainId: "10",
    evmChainId: 10,
    internalId: "optimism",
    mobulaName: "optimism",
    displayName: "Optimism",
    isEvm: true,
    explorerUrl: "https://optimistic.etherscan.io",
  },
  {
    chainId: "137",
    evmChainId: 137,
    internalId: "polygon",
    mobulaName: "polygon",
    displayName: "Polygon",
    isEvm: true,
    explorerUrl: "https://polygonscan.com",
  },
  {
    chainId: "4663",
    evmChainId: 4663,
    internalId: "robinhood",
    // Mobula rejects "robinhood" (400 Invalid blockchain); it accepts "robinhood-chain" or "evm:4663".
    mobulaName: "robinhood-chain",
    displayName: "Robinhood Chain",
    isEvm: true,
    explorerUrl: "https://robinhoodchain.blockscout.com",
  },
] as const;

export const UNIVERSAL_NATIVE_TOKEN_ADDRESS = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";
export const SOLANA_NATIVE_TOKEN_ADDRESS = "So11111111111111111111111111111111111111111";
export const SOLANA_WRAPPED_SOL_ADDRESS = "So11111111111111111111111111111111111111112";
export const SOLANA_SYSTEM_PROGRAM_ADDRESS = "11111111111111111111111111111111";

/**
 * Display metadata for wrapped native assets. Mobula can return wrapped native
 * tokens with the plain native symbol, so the swap selector rewrites labels
 * from this table to avoid duplicate "SOL" / "ETH" rows.
 */
const WRAPPED_NATIVE_TOKENS: Record<string, WrappedNativeTokenConfig> = {
  "1": {
    address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    symbol: "WETH",
    name: "Wrapped Ether",
  },
  "10": {
    address: "0x4200000000000000000000000000000000000006",
    symbol: "WETH",
    name: "Wrapped Ether",
  },
  "56": {
    address: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
    symbol: "WBNB",
    name: "Wrapped BNB",
  },
  "137": {
    address: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270",
    symbol: "WMATIC",
    name: "Wrapped Matic",
  },
  "4663": {
    address: "0x0bd7d308f8e1639fab988df18a8011f41eacad73",
    symbol: "WETH",
    name: "Wrapped Ether",
  },
  "42161": {
    address: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
    symbol: "WETH",
    name: "Wrapped Ether",
  },
  "8453": {
    address: "0x4200000000000000000000000000000000000006",
    symbol: "WETH",
    name: "Wrapped Ether",
  },
  "solana": {
    address: SOLANA_WRAPPED_SOL_ADDRESS,
    symbol: "WSOL",
    name: "Wrapped SOL",
  },
};

// ============================================================================
// Auto-derived mappings (computed from CHAIN_CONFIG)
// ============================================================================

/**
 * All supported chain IDs as array
 */
export const SUPPORTED_CHAIN_IDS = CHAIN_CONFIG.map((c) => c.chainId);

/**
 * Chain ID to internal ID mapping (for CDN logos)
 * e.g., "8453" -> "base", "solana" -> "solana"
 */
export const CHAIN_ID_TO_INTERNAL: Record<string, InternalChainId> = Object.fromEntries(
  CHAIN_CONFIG.map((c) => [c.chainId, c.internalId]),
);

/**
 * Chain ID to Mobula blockchain name mapping (for Mobula API)
 * e.g., "8453" -> "base", "solana" -> "solana"
 */
export const CHAIN_ID_TO_MOBULA: Record<string, string> = Object.fromEntries(
  CHAIN_CONFIG.map((c) => [c.chainId, c.mobulaName]),
);

/**
 * Chain ID to display name mapping (for UI)
 * e.g., "8453" -> "Base", "56" -> "BNB Chain"
 */
export const CHAIN_ID_TO_DISPLAY_NAME: Record<string, string> = Object.fromEntries(
  CHAIN_CONFIG.map((c) => [c.chainId, c.displayName]),
);

/**
 * Internal ID to chain ID mapping (reverse lookup)
 * e.g., "base" -> "8453", "solana" -> "solana"
 */
export const INTERNAL_ID_TO_CHAIN_ID: Record<string, string> = Object.fromEntries(
  CHAIN_CONFIG.map((c) => [c.internalId, c.chainId]),
);

/**
 * Set of supported chain IDs for O(1) lookup
 */
export const SUPPORTED_CHAIN_ID_SET = new Set(SUPPORTED_CHAIN_IDS);

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Check if a chain ID is supported
 * @param chainId - Chain ID to check (e.g., "8453", "solana")
 * @returns true if the chain is supported
 */
export const isSupportedChain = (chainId: string): boolean => {
  return SUPPORTED_CHAIN_ID_SET.has(chainId);
};

/**
 * Get chain config by chain ID
 * @param chainId - Chain ID (e.g., "8453", "solana")
 * @returns Chain config entry or undefined
 */
export const getChainConfig = (chainId: string): ChainConfigEntry | undefined => {
  return CHAIN_CONFIG.find((c) => c.chainId === chainId);
};

/**
 * Get chain config by internal ID
 * @param internalId - Internal chain ID (e.g., "base", "solana")
 * @returns Chain config entry or undefined
 */
export const getChainByInternalId = (internalId: string): ChainConfigEntry | undefined => {
  return CHAIN_CONFIG.find((c) => c.internalId === internalId);
};

/**
 * Get Mobula blockchain name from chain ID
 * @param chainId - Chain ID (e.g., "8453", "solana")
 * @returns Mobula blockchain name or undefined
 */
export const getMobulaChainName = (chainId: string): string | undefined => {
  return CHAIN_ID_TO_MOBULA[chainId];
};

/**
 * Get internal chain ID from chain ID (for CDN logos)
 * @param chainId - Chain ID (e.g., "8453", "solana")
 * @returns Internal chain ID or undefined
 */
export const getInternalChainId = (chainId: string): InternalChainId | undefined => {
  return CHAIN_ID_TO_INTERNAL[chainId];
};

/**
 * Get display name from chain ID
 * @param chainId - Chain ID (e.g., "8453", "solana")
 * @returns Display name (e.g., "Base") or the chainId if not found
 */
export const getChainDisplayName = (chainId: string): string => {
  return CHAIN_ID_TO_DISPLAY_NAME[chainId] ?? chainId;
};

/**
 * Get EVM chains only
 * @returns Array of EVM chain configs
 */
export const getEvmChains = (): ChainConfigEntry[] => {
  return CHAIN_CONFIG.filter((c) => c.isEvm);
};

/**
 * Get non-EVM chains (e.g., Solana)
 * @returns Array of non-EVM chain configs
 */
export const getNonEvmChains = (): ChainConfigEntry[] => {
  return CHAIN_CONFIG.filter((c) => !c.isEvm);
};

/**
 * Check if a chain is EVM-compatible
 * @param chainId - Chain ID (e.g., "8453", "solana")
 * @returns true if EVM chain, false otherwise (defaults to true for unknown chains)
 */
export const isEvmChain = (chainId: string): boolean => {
  const config = getChainConfig(chainId);
  // Default to true for backwards compatibility with unknown EVM chains
  return config?.isEvm ?? true;
};

/**
 * Returns the wrapped native token metadata for a chain, if we track one.
 * Used only for selector/display labeling.
 */
export const getWrappedNativeToken = (chainId: string): WrappedNativeTokenConfig | undefined => {
  return WRAPPED_NATIVE_TOKENS[chainId];
};

/**
 * Checks the cross-chain native sentinel used by Mobula and parts of our API.
 */
export const isUniversalNativeTokenAddress = (address: string): boolean => {
  return normalizeAddress(address) === UNIVERSAL_NATIVE_TOKEN_ADDRESS.toLowerCase();
};

/**
 * Detects whether an address should be treated as the native gas token on a
 * given chain.
 *
 * Solana is the only special case here because Mobula/native flows can surface
 * more than one native alias:
 * - `So111...11` for native SOL
 * - `111111...111` from the system program in some responses
 * - `0xEeee...` from the universal native shortcut
 */
export const isNativeTokenAddress = (address: string, chainId: string): boolean => {
  if (!address) return false;

  const normalizedAddress = normalizeAddress(address);
  if (chainId === "solana") {
    return (
      normalizedAddress === SOLANA_NATIVE_TOKEN_ADDRESS ||
      normalizedAddress === SOLANA_SYSTEM_PROGRAM_ADDRESS ||
      isUniversalNativeTokenAddress(normalizedAddress)
    );
  }

  return isUniversalNativeTokenAddress(normalizedAddress);
};

/**
 * Detects the wrapped version of the chain's native token, such as WSOL/WETH.
 */
export const isWrappedNativeTokenAddress = (address: string, chainId: string): boolean => {
  if (!address) return false;

  const wrappedToken = getWrappedNativeToken(chainId);
  if (!wrappedToken) return false;

  return normalizeAddress(address) === normalizeAddress(wrappedToken.address);
};

/**
 * Canonicalizes token addresses only for Mobula swap/token-metadata requests.
 *
 * This is intentionally not a selector helper. The selector keeps real token
 * addresses, while the Mobula client translates aliases into the single address
 * shape Mobula expects.
 */
export const getCanonicalSwapTokenAddress = (address: string, chainId: string): string => {
  if (!address) return address;

  if (chainId === "solana") {
    if (isNativeTokenAddress(address, chainId)) {
      return SOLANA_NATIVE_TOKEN_ADDRESS;
    }

    if (isWrappedNativeTokenAddress(address, chainId)) {
      return SOLANA_WRAPPED_SOL_ADDRESS;
    }
  }

  if (isUniversalNativeTokenAddress(address)) {
    return UNIVERSAL_NATIVE_TOKEN_ADDRESS.toLowerCase();
  }

  return normalizeAddress(address);
};

/**
 * Normalize a blockchain address based on address format.
 * - EVM addresses (0x prefix): lowercased (case-insensitive)
 * - Solana addresses (base58): preserved (case-sensitive)
 *
 * @param address - The address to normalize
 * @returns Normalized address
 */
export const normalizeAddress = (address: string): string => {
  if (!address) return address;
  // EVM addresses start with 0x, Solana uses base58 (no 0x prefix)
  return address.startsWith("0x") ? address.toLowerCase() : address;
};

// ============================================================================
// Address Validation Functions
// ============================================================================

/**
 * Check if a string is a valid EVM address (0x prefixed, 40 hex characters)
 * @param address - The address to validate
 * @returns true if valid EVM address
 */
export const isValidEvmAddress = (address: string): boolean => {
  if (!address) return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Check if a string is a valid Solana address (base58 encoded, 32-44 chars)
 * Solana public keys are base58-encoded (no 0, O, I, l characters) and typically 32-44 characters
 * @param address - The address to validate
 * @returns true if valid Solana address
 */
export const isValidSolanaAddress = (address: string): boolean => {
  if (!address) return false;
  // Base58 alphabet: 1-9, A-H, J-N, P-Z, a-k, m-z (excludes 0, O, I, l)
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
};

/**
 * Check if an address looks like an EVM address (starts with 0x)
 * This is a quick check, not full validation. Use isValidEvmAddress for full validation.
 * @param address - The address to check
 * @returns true if address starts with 0x (EVM-style)
 */
export const isEvmAddress = (address: string): boolean => {
  if (!address) return false;
  return address.startsWith("0x");
};

/**
 * Check if a string is a valid blockchain address for the given chain
 * @param address - The address to validate
 * @param chainId - Optional chain ID. If "solana", validates as Solana address.
 *                  If not provided, auto-detects based on address format.
 * @returns true if valid address for the chain
 */
export const isValidBlockchainAddress = (address: string, chainId?: string): boolean => {
  if (!address) return false;

  // If chainId is explicitly Solana, validate as Solana address
  if (chainId === "solana") {
    return isValidSolanaAddress(address);
  }

  // If chainId is provided and not Solana, validate as EVM
  if (chainId) {
    return isValidEvmAddress(address);
  }

  // Auto-detect: if starts with 0x, try EVM; otherwise try Solana
  if (address.startsWith("0x")) {
    return isValidEvmAddress(address);
  }

  return isValidSolanaAddress(address);
};
